import React, { useState, useRef, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ListBox } from "primereact/listbox";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import api from "../../Services/api";

const UserDialog = ({
  dialogVisible,
  setDialogVisible,
  selectedUser = null,
  refreshUsers,
}) => {
  const [addFormDialogVisible, setAddFormDialogVisible] = useState(false);
  const [selectedForms, setSelectedForms] = useState([]);
  const [allForms, setAllForms] = useState([]);
  const [userForms, setUserForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);

  // Fetch forms when dialog opens or when a form is added/removed
  useEffect(() => {
    if (dialogVisible && selectedUser) {
      fetchAllForms();
      fetchUserForms();
    } else {
      // Clear state when dialog closes
      setAllForms([]);
      setUserForms([]);
      setSelectedForms([]);
    }
    // eslint-disable-next-line
  }, [dialogVisible, selectedUser]);

  // Fetch all forms from the backend
  const fetchAllForms = async () => {
    try {
      const response = await api.get("/forms/all");
      const forms =
        response.data.forms.filter((form) => !form.softDelete) || [];
      setAllForms(forms);
    } catch (error) {
      console.error("Error fetching forms:", error);
    }
  };

  // Fetch forms assigned to the user
  const fetchUserForms = async () => {
    if (!selectedUser) return;

    try {
      const response = await api.get(`/forms/user/${selectedUser.id}`);
      const forms = response.data.forms || [];
      setUserForms(forms);
    } catch (error) {
      console.error("Error fetching user forms:", error);
      // If 403 or 204, it's not a critical error, just set empty forms
      if (
        error.response &&
        (error.response.status === 403 || error.response.status === 204)
      ) {
        setUserForms([]);
      }
    }
  };

  // Forms that aren't already assigned to the user
  const availableForms = allForms.filter(
    (form) => !userForms.some((userForm) => userForm.id === form.id)
  );

  // Handle saving selected forms to the user
  const handleSaveForms = async () => {
    if (!selectedUser || selectedForms.length === 0) return;

    setLoading(true);
    const successForms = [];
    const failedForms = [];

    // Process each selected form
    for (const form of selectedForms) {
      try {
        // Convert userId to string explicitly
        const userId = String(selectedUser.id);
        // eslint-disable-next-line
        const response = await api.put(`/forms/${form.id}/update-assigned`, {
          action: "add",
          userId: userId,
        });

        successForms.push(form.title);
      } catch (error) {
        console.error(`Error assigning form ${form.title}:`, error);
        console.error(
          "Error details:",
          error.response?.data || "No response data available"
        );
        failedForms.push(form.title);
      }
    }

    setLoading(false);

    // Show success message for successful assignments
    if (successForms.length > 0) {
      toast.current.show({
        severity: "success",
        summary: "Forms Added",
        detail: `Successfully added: ${successForms.join(", ")}`,
        life: 4000,
      });

      // Refresh the forms lists
      fetchAllForms();
      fetchUserForms();
    }

    // Show error message for failed assignments
    if (failedForms.length > 0) {
      toast.current.show({
        severity: "error",
        summary: "Failed to Add",
        detail: `Failed to add: ${failedForms.join(", ")}`,
        life: 4000,
      });
    }

    // Close dialog and reset selected forms
    setAddFormDialogVisible(false);
    setSelectedForms([]);
  };

  // Show confirmation before removing a form
  const confirmRemoveForm = (event, form) => {
    confirmPopup({
      target: event.currentTarget,
      message: `Are you sure you want to remove "${form.title}" from this user's access?`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleRemoveForm(form),
      reject: () => {
        // Optional: show a message that the action was cancelled
        toast.current.show({
          severity: "info",
          summary: "Cancelled",
          detail: "Form removal cancelled",
          life: 2000,
        });
      },
    });
  };

  // Handle removing a form from user access
  const handleRemoveForm = async (form) => {
    if (!selectedUser) return;

    try {
      // Convert userId to string explicitly
      const userId = String(selectedUser.id);
      // eslint-disable-next-line
      const response = await api.put(`/forms/${form.id}/update-assigned`, {
        action: "remove",
        userId: userId,
      });

      toast.current.show({
        severity: "success",
        summary: "Form Removed",
        detail: `${form.title} was removed from user's access`,
        life: 3000,
      });

      // Refresh the forms lists
      fetchAllForms();
      fetchUserForms();
    } catch (error) {
      console.error(`Error removing form ${form.title}:`, error);
      console.error(
        "Error details:",
        error.response?.data || "No response data available"
      );
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to remove ${form.title}`,
        life: 3000,
      });
    }
  };

  // Template for form item in the list with remove button
  const formItemTemplate = (form) => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0.5rem 0",
        }}
      >
        <span>{form.title}</span>
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-danger p-button-text"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            confirmRemoveForm(e, form);
          }}
          tooltip="Remove access"
        />
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmPopup />
      <Dialog
        visible={dialogVisible}
        style={{ width: window.innerWidth > 768 ? "60vw" : "95vw" }}
        modal
        onHide={() => setDialogVisible(false)}
        header={`User Forms - ${selectedUser?.username || ""}`}
        className="user-dialog"
      >
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3>Forms Access</h3>
            <Button
              label="Add Form To User"
              icon="pi pi-plus"
              className="p-button-sm p-button-primary"
              onClick={() => setAddFormDialogVisible(true)}
              style={{
                backgroundColor: "#0D2E4D",
                border: "none",
                color: "#fff",
                fontFamily: "poppins",
              }}
              disabled={!availableForms.length}
              tooltip={!availableForms.length ? "No more forms to add" : ""}
            />
          </div>
          {userForms && userForms.length > 0 ? (
            <ListBox
              value={null}
              options={userForms}
              optionLabel="title"
              itemTemplate={formItemTemplate}
              style={{ width: "100%", marginTop: "1rem" }}
              listStyle={{ maxHeight: "300px" }}
            />
          ) : (
            <p style={{ marginTop: "1rem", color: "#666" }}>
              No forms assigned to this user.
            </p>
          )}
        </div>

        <Dialog
          visible={addFormDialogVisible}
          style={{ width: window.innerWidth > 768 ? "60vw" : "95vw" }}
          modal
          onHide={() => setAddFormDialogVisible(false)}
          header="Add Forms"
          className="user-dialog"
        >
          <div>
            <MultiSelect
              value={selectedForms}
              options={availableForms}
              onChange={(e) => setSelectedForms(e.value)}
              optionLabel="title"
              placeholder="Select forms"
              style={{ width: "100%" }}
              emptyMessage="No additional forms available"
              filter
              disabled={loading}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "1rem",
            }}
          >
            <Button
              label="Cancel"
              className="p-button-text"
              style={{
                backgroundColor: "#D97A07",
                border: "none",
                color: "#fff",
                marginLeft: "10px",
                fontFamily: "poppins",
              }}
              onClick={() => setAddFormDialogVisible(false)}
              disabled={loading}
            />
            <Button
              label={loading ? "Saving..." : "Save"}
              className="p-button-primary"
              onClick={handleSaveForms}
              style={{
                backgroundColor: "#0D2E4D",
                border: "none",
                color: "#fff",
                fontFamily: "poppins",
                marginLeft: "10px",
              }}
              disabled={loading || selectedForms.length === 0}
              loading={loading}
            />
          </div>
        </Dialog>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "1rem",
          }}
        >
          <Button
            label="Close"
            className="p-button-text"
            onClick={() => setDialogVisible(false)}
            style={{
              backgroundColor: "#D97A07",
              border: "none",
              color: "#fff",
              marginLeft: "10px",
              fontFamily: "poppins",
            }}
          />
        </div>
      </Dialog>
    </>
  );
};

export default UserDialog;
